<script setup lang="ts">
  import { computed, inject, onMounted, ref, watch, type Ref } from 'vue'
  import { Button } from '@/components/ui/button/index.ts'
  import { useRouter } from 'vue-router'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog/index.ts'
  import TypeBasedTransition from '@/components/questionaire/transitions/typeBasedTransition'
  import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'
  import { ROUTES } from '@/router/routes'
  import {
    QuestionnaireSubmissionsService,
    type ClientQuestionnaireSubmission
  } from '@/types/generated'
  import { useToast } from '@/components/ui/toast/use-toast'

  const { t } = useTranslation({
    en: {
      thankYouForYourInformation: 'Thank you for your information.',
      pleaseTakeTimeToReview: 'Please take a moment to review your information.',
      optional: '(optional)',
      noInformation: 'No information provided',
      edit: 'Edit',
      warning: 'Warning',
      notAllQuestionsFilled: 'You have not filled out all questions yet.',
      submitQuestionnaire: 'Submit Questionnaire',
      submitQuestionnaireAnyway: 'Submit Questionnaire Anyway',
      submitQuestionnaireTitle: 'Submit Questionnaire',
      submitQuestionnaireDescription:
        'Do you want to submit the questionnaire now? After submission, you can no longer change your information.',
      cancel: 'Cancel',
      yesSubmit: 'Yes, submit'
    },
    de: {
      thankYouForYourInformation: 'Vielen Dank für Ihre Angaben.',
      pleaseTakeTimeToReview: 'Bitte nehmen Sie sich kurz Zeit, die Angaben zu überprüfen.',
      optional: '(optional)',
      noInformation: 'Keine Angabe',
      edit: 'Bearbeiten',
      warning: 'Achtung',
      notAllQuestionsFilled: 'Sie haben noch nicht alle Fragen ausgefüllt.',
      submitQuestionnaire: 'Fragebogen absenden',
      submitQuestionnaireAnyway: 'Fragebogen trotzdem absenden',
      submitQuestionnaireTitle: 'Fragebogen absenden',
      submitQuestionnaireDescription:
        'Möchten Sie den Fragebogen nun absenden? Nach dem Absenden können Sie Ihre Angaben nicht mehr ändern.',
      cancel: 'Abbrechen',
      yesSubmit: 'Ja, absenden'
    }
  })

  const { toast } = useToast()
  const props = defineProps({
    isLastField: {
      type: Boolean,
      default: false
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    },
    formFields: {
      type: Array as () => any[],
      required: true
    },
    isExternal: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['cellClick'])

  const isNotComplete = ref(false)

  const router = useRouter()

  const themeColor = computed(() => useThemeColor().value)

  const submission = inject<Ref<ClientQuestionnaireSubmission | null>>('questionnaire', ref(null))
  const accessToken = inject<Ref<string | null>>('accessToken', ref(null))
  const sessionToken = inject<Ref<string | null>>('sessionToken', ref(null))
  const refreshData = inject<() => Promise<void>>('refreshData')

  onMounted(() => {
    const existing = localStorage.getItem('shouldShowJumpButton')
    let shouldShowObject = existing ? JSON.parse(existing) : {}

    shouldShowObject[submission.value?.uuid || ''] = true

    localStorage.setItem('shouldShowJumpButton', JSON.stringify(shouldShowObject))

    checkCompletion()
  })

  watch(
    () => submission.value?.schema,
    () => {
      checkCompletion()
    },
    { deep: true }
  )

  watch(
    () => props.formFields,
    () => {
      checkCompletion()
    },
    { deep: true }
  )

  const hasValue = (field: any) => {
    const submissionValue = getSubmissionValue(field)

    if (submissionValue === undefined || submissionValue === null) {
      return false
    }

    const hasEmptyAsChoice = field && field.choices && field.choices.includes('')

    if (Array.isArray(submissionValue)) {
      return submissionValue.length > 0
    }

    if (typeof submissionValue === 'object') {
      return Object.keys(submissionValue).length > 0
    }

    return submissionValue !== '' || hasEmptyAsChoice
  }

  const isRequired = (field: any) => !field.optional

  const getSubmissionValue = (field: any) => {
    if (!submission.value?.schema || !Array.isArray(submission.value.schema)) return undefined

    for (const page of submission.value.schema) {
      if (page.items && Array.isArray(page.items)) {
        for (const item of page.items) {
          if (item.uuid === field.uuid) {
            return item.value
          }
        }
      }
    }
    return undefined
  }

  const getValue = (field: any) => {
    const value = getSubmissionValue(field)

    if (field.type === 'filefield') {
      let uploadedFiles: any[] = []
      if (Array.isArray(value)) {
        value.forEach((file) => {
          uploadedFiles.push({
            name: file.upload ? file.upload.filename : file.filename,
            url: file.url
          })
        })
        return uploadedFiles
      }
      return []
    }

    if (field.type === 'yesno') {
      return value === true ? 'ja' : value === false ? 'nein' : ''
    }
    return value
  }

  const checkCompletion = () => {
    isNotComplete.value = props.formFields.some((field) => isRequired(field) && !hasValue(field))
  }

  const submitForm = async () => {
    if (props.isPreviewMode) {
      return
    }
    try {
      await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
        uuid: submission.value?.uuid || '',
        requestBody: {
          schema: submission.value?.schema,
          closed: true
        }
      })

      if (refreshData) {
        await refreshData()
      }
    } catch (error) {
      console.error('Error updating submission:', error)
      toast({
        title: 'Oh, oh!',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      })
    }
    if (props.isExternal) {
      router.push({ path: `${ROUTES.CLIENT_PORTAL.EXTERNAL_COMPLETED}` })
    } else {
      const currentPath = router.currentRoute.value.path
      if (currentPath.includes('/questionnaire/')) {
        return
      } else {
        // Build query params - prefer session_token if available, fallback to access_token
        const queryParams: Record<string, string> = {}
        if (sessionToken.value) {
          queryParams.session_token = sessionToken.value
        } else if (accessToken.value) {
          queryParams.access_token = accessToken.value
        }
        router.push({
          path: `${ROUTES.CLIENT_PORTAL.HOME}`,
          query: queryParams
        })
      }
    }
  }

  function handleCellClick(fieldUuid: string) {
    emit('cellClick', fieldUuid)
  }

  defineExpose({ handleCellClick })
</script>

<template>
  <TypeBasedTransition :transition-type="'fadeUpDown'">
    <div
      class="mt-[200px] flex h-full w-full justify-center py-[15%] max-sm:py-[1px] sm:mt-3 md:py-[15%]
        lg:py-[10%]"
    >
      <div class="flex h-full w-full flex-col justify-center px-[10%] md:px-[18%]">
        <h1 class="text-md text-center font-bold sm:text-lg">
          {{ t('thankYouForYourInformation') }}
        </h1>
        <p class="pb-6 pt-3 text-center text-gray-500">
          {{ t('pleaseTakeTimeToReview') }}
        </p>
        <div class="grid grid-cols-1 lg:grid-cols-1 lg:justify-center">
          <div class="space-y-3">
            <div
              v-for="field in formFields"
              :key="field.uuid"
              data-cy="formResultRow"
              class="group relative flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-3"
            >
              <!-- Display green check if completed -->
              <div
                v-if="hasValue(field)"
                class="h-9 w-9 rounded-lg object-cover text-green-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="h-9 w-9"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <!-- Display gray icon if not completed and not required -->
              <div
                v-else-if="!hasValue(field) && !isRequired(field)"
                class="h-9 w-9 rounded-lg object-cover text-gray-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="h-9 w-9"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <!-- Display red icon if not completed and required -->
              <div
                v-if="!hasValue(field) && isRequired(field)"
                class="h-9 w-9 rounded-lg object-cover text-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="h-9 w-9"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <div class="grow truncate">
                <p class="block truncate text-sm font-semibold text-gray-800">
                  {{ field.label }}
                  <span
                    v-if="!isRequired(field)"
                    class="text-xs text-gray-500"
                  >
                    {{ t('optional') }}
                  </span>
                </p>
                <p class="block truncate text-xs text-gray-500">
                  <template v-if="Array.isArray(getValue(field))">
                    <ScrollArea class="">
                      <div
                        v-for="(item, index) in getValue(field)"
                        :key="index"
                      >
                        <span v-if="typeof item == 'string'">
                          {{ item }}
                        </span>
                        <span v-else>
                          <a
                            :href="item.url"
                            target="_blank"
                            class="text-blue-500 hover:underline"
                          >
                            {{ item.name || item.filename }}
                          </a>
                          <span v-if="index < getValue(field).length - 1">,</span>
                        </span>
                      </div>
                      <ScrollBar
                        orientation="horizontal"
                        class=""
                      />
                    </ScrollArea>
                  </template>
                  <template v-else-if="getValue(field) !== ''">
                    {{ getValue(field) }}
                  </template>
                  <template v-else>{{ t('noInformation') }}</template>
                </p>
              </div>

              <!-- More Dropdown -->
              <div class="lg:absolute lg:end-3 lg:top-3">
                <div
                  class="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 sm:p-1 lg:shadow"
                >
                  <!-- Button Icon -->
                  <div class="hs-tooltip inline-block">
                    <button
                      type="button"
                      class="hs-tooltip-toggle inline-flex size-[30px] items-center justify-center gap-x-2 rounded-lg border
                        border-transparent text-gray-500 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
                        disabled:pointer-events-none disabled:opacity-50"
                      data-hs-overlay="#hs-pro-dupfmsh"
                      @click.stop="handleCellClick(field.uuid)"
                    >
                      <svg
                        class="size-4 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                        />
                      </svg>
                    </button>

                    <span
                      class="hs-tooltip-content invisible absolute z-20 inline-block rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs
                        text-white opacity-0 hs-tooltip-shown:visible hs-tooltip-shown:opacity-100"
                      role="tooltip"
                    >
                      {{ t('edit') }}
                    </span>
                  </div>
                  <!-- End Button Icon -->
                </div>
              </div>
              <!-- End More Dropdown -->
            </div>
          </div>
        </div>
        <div class="mt-3 flex items-center justify-center">
          <span
            v-if="isNotComplete"
            class="rounded border-l-4 border-red-500 p-2"
          >
            <b>{{ t('warning') }}</b>
            : {{ t('notAllQuestionsFilled') }}
          </span>
        </div>
        <AlertDialog>
          <AlertDialogTrigger
            v-if="!isPreviewMode"
            as-child
          >
            <div class="flex items-center justify-center">
              <Button
                data-cy="questionnaireSubmitBtn"
                class="my-10 w-[250px]"
                :class="
                  isNotComplete
                    ? 'bg-red-600 hover:bg-red-700'
                    : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                "
              >
                {{ isNotComplete ? t('submitQuestionnaireAnyway') : t('submitQuestionnaire') }}
              </Button>
            </div>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{{ t('submitQuestionnaireTitle') }}</AlertDialogTitle>
              <AlertDialogDescription>
                {{ t('submitQuestionnaireDescription') }}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{{ t('cancel') }}</AlertDialogCancel>
              <AlertDialogAction
                data-cy="questionnaireConfirmSubmitBtn"
                :disabled="isPreviewMode"
                :class="
                  isNotComplete
                    ? 'bg-red-600 hover:bg-red-700'
                    : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                "
                @click="submitForm"
              >
                {{ t('yesSubmit') }}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  </TypeBasedTransition>
</template>
